<template>
  <div
    id="emails-input"
    class="w-full h-full"
    @keydown="eventHandler"
    @keypress="eventHandler"
    @paste="eventHandler"
    @blur="eventHandler"
    @click="eventHandler"
  >
    <div ref="emails-input" class="emails emails-input w-full h-full">
      <input
        type="text"
        role="emails-input"
        :placeholder="placeholder"
        :class="emails.length > 0 ? 'w-auto' : 'w-full'"
      />
    </div>
  </div>
</template>

<script>
import { validateEmail } from '../../../../../js/util/helper.js'

export default {
  data() {
    return {
      keycodes: { comma: 44, enter: 13, backspace: 8, space: 32 },
      triggerKeyCodes: [],
      pasteSplitPattern: /(?:,| )+/,
      inputContainerNode: '#emails-input',
      emails: [],
    }
  },

  computed: {
    placeholder() {
      if (this.emails.length) {
        return 'Add more emails...'
      }
      return 'email@example.com, email2@example.com, ...'
    },
  },

  watch: {
    emails: {
      handler: function () {
        this.$emit('input-updated', this.emails)
      },

      deep: true,
    },
  },

  mounted() {
    this.triggerKeyCodes = [
      this.keycodes.enter,
      this.keycodes.comma,
      this.keycodes.space,
    ]
  },

  methods: {
    getEmails() {
      return this.getChips()
        ?.filter(function (chip) {
          return !chip.classList.contains('invalid')
        })
        .map(function (chip) {
          return chip.firstChild.textContent
        })
    },

    getChips() {
      const nodes = this.$refs['emails-input']?.querySelectorAll(
        'span[ref="email-chip"]',
      )
      if (nodes) {
        return Array.prototype.slice.call(nodes)
      }
      return []
    },

    eventHandler(event) {
      const type = event.type
      const fnName = `${type}Handler`

      if (this[fnName]) {
        this[fnName](event)
        const emails = this.getEmails()
        this.$set(this, 'emails', emails)
      }
    },

    keydownHandler(event) {
      // delete last element
      if (event.keyCode === this.keycodes.backspace && !event.target.value) {
        // deal with DOM
        const chips = this.getChips()
        if (!chips.length) {
          return
        }
        const lastChip = chips[chips.length - 1]
        lastChip.remove()

        // // deal with Vue state
        // this.emails.splice(this.emails.length)
      }
    },

    keypressHandler(event) {
      if (this.triggerKeyCodes.indexOf(event.keyCode) < 0) {
        return
      }
      event.preventDefault()
      this.addChip(event.target, event.target.value)
    },

    pasteHandler(event) {
      const that = this
      if (!event.target.matches('input')) {
        return
      }

      event.preventDefault()

      const chunks = event.clipboardData
        .getData('Text')
        .split(this.pasteSplitPattern)
      if (chunks.length > 1) {
        chunks.forEach(function (chunk) {
          that.addChip(event.target, chunk)
        })
        return
      }

      const chunk = chunks[0]
      if (validateEmail(chunk)) {
        this.addChip(event.target, chunk)
        return
      }

      event.target.value += chunk
    },

    //@todo
    blurHandler(event) {
      this.addChip(event.target, event.target.value)
    },

    clickHandler(event) {
      if (event.target.classList.contains('emails-input')) {
        event.target.querySelector('input').focus()
      }

      if (event.target.classList.contains('remove')) {
        const parent = event.target.parentNode
        // update DOM
        this.$refs['emails-input'].removeChild(parent)
      }
    },

    addChip(refElement, email) {
      const trimmedEmail = email && email.trim()
      if (!trimmedEmail) {
        return
      }

      const emailObj = {
        value: trimmedEmail,
        valid: validateEmail(trimmedEmail),
      }

      // update DOM
      const chip = document.createElement('span')
      chip.setAttribute('role', 'email-chip')
      chip.setAttribute('ref', 'email-chip')
      chip.setAttribute('key', email)
      chip.classList.add('email-chip')
      if (!emailObj.valid) {
        chip.classList.add('invalid')
      }
      chip.innerHTML =
        '<span class="content">' +
        trimmedEmail +
        '</span><a href="#" class="remove">×</a>'

      refElement.parentNode.insertBefore(chip, refElement)
      refElement.value = ''
    },
  },
}
</script>

<style>
#emails-input {
  padding-top: 1.5rem;
}

.emails.emails-input {
  max-height: 10rem;
  box-sizing: border-box;
  padding-top: 0;
  padding-left: 0.9rem;
  padding-right: 0.9rem;
  padding-bottom: 0.5rem;
  line-height: 1.5rem;
  font-size: 0.875rem;
  cursor: text;
  overflow: auto;
}

.emails.emails-input .email-chip {
  box-sizing: border-box;
  position: relative;
  display: inline-block;
  background: rgba(102, 153, 255, 0.2);
  vertical-align: top;
  border-radius: 6.25rem;
  padding-left: 0.625rem;
  padding-right: 1.5rem;
  margin: 0.125rem;
  max-width: 100%;
  overflow: hidden;
}

.emails.emails-input .email-chip .content {
  display: inline-block;
  vertical-align: top;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.emails.emails-input .email-chip .remove {
  text-decoration: none;
  color: inherit;
  text-align: center;
  position: absolute;
  cursor: pointer;
  width: 1rem;
  font-size: 1rem;
  user-select: none;
  -moz-user-select: none;
  -khtml-user-select: none;
  -webkit-user-select: none;
  -o-user-select: none;
}

.emails.emails-input .email-chip.invalid {
  background: #fff;
  border-bottom: 1px dashed #d92929;
  border-radius: 0;
  padding-left: 0;
  padding-right: 1rem;
}

.emails.emails-input input {
  border: 0;
  line-height: inherit;
  font-size: inherit;
  color: inherit;
  margin: 0.125rem;
}

.emails.emails-input input::placeholder,
.emails.emails-input input::-ms-input-placeholder,
.emails.emails-input input:-ms-input-placeholder {
  color: #c3c2cf;
  opacity: 1;
}

.emails.emails-input input:focus {
  outline: none;
}
</style>
